import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  HTMLProps,
  ReactNode,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import { AnimationWrapper } from '../../../AnimationWrapper'
import { CommentsIcon } from '../../../Icons/Miscellaneous/Comments'
import { BaseLink } from '../../../Links'
import { TypographyV2 } from '../../../Seatbelt2/Foundations/Typography'
import styles from '../ArticleCard.module.scss'

export const ArticleCardText: FunctionComponent<Props> = ({
  href,
  title,
  strapline,
  date,
  includeCommentsIcon = false,
  children,
  className,
  noTitleAnimation = false,
  linkProps,
  ...rest
}) => {
  const TitleAndStrapline = () => (
    <>
      <TypographyV2
        tag="h2"
        textStyle="sb-text-interface-heading-large"
        className={styles.articleCardTitle}
        data-testid="article-card-text-title"
      >
        {noTitleAnimation ? (
          title
        ) : (
          <AnimationWrapper inverted={true}>{title}</AnimationWrapper>
        )}
      </TypographyV2>
      {strapline && (
        <TypographyV2
          textStyle="sb-text-interface-body-default-regular"
          className={styles.articleCardStrapline}
          data-testid="article-card-text-strapline"
        >
          {strapline}
        </TypographyV2>
      )}
    </>
  )

  return (
    <div
      className={classnames(styles.articleCardText, className)}
      data-testid="article-card-text"
      {...rest}
    >
      {href ? (
        <BaseLink
          href={href}
          className={classnames(
            styles.articleCardTextLink,
            noTitleAnimation && styles.articleCardTextBlackLink
          )}
          invertedUnderline={true}
          data-testid="article-card-text-link"
          {...linkProps}
        >
          <TitleAndStrapline />
        </BaseLink>
      ) : (
        <TitleAndStrapline />
      )}
      <div className={styles.articleCardExtraContent}>
        {(date && (
          <div
            className={classnames(
              includeCommentsIcon && styles.articleCardTextDateWithIcon
            )}
          >
            {includeCommentsIcon && (
              <CommentsIcon
                className={styles.articleCardTextDateIcon}
                data-testid="article-card-text-comment-icon"
              />
            )}
            <TypographyV2
              textStyle="sb-text-interface-body-x-small-regular"
              data-testid="article-card-text-date"
            >
              {date}
            </TypographyV2>
          </div>
        )) ||
          children}
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Child nodes that will be rendered inside the article card
   */
  children?: ReactNode
  /**
   * Class name that can be added to the component for additional styling purposes
   */
  className?: string
  /**
   * Article date to appear at the bottom of the molecule.
   * N.B This will not appear if any children are passed to the component.
   */
  date?: string
  /**
   * Link that will be visited when the card is clicked on.
   * This should only be provided when the molecule is being used outside the Article Card Component
   */
  href?: string
  /**
   * Optional comments icon
   * When a date is rendered if this prop is true a comment icon will sit to the left of the date.
   */
  includeCommentsIcon?: boolean
  /**
   * Props to be passed through to the BaseLink
   * E.g. for data properties for tracking purposes
   */
  linkProps?: ComponentPropsWithoutRef<typeof BaseLink>
  /**
   * When passing an href, setting this to true will stop the heading underline animation.
   * In this scenario, an element in the children should display a hover animation instead.
   */
  noTitleAnimation?: boolean
  /**
   * Strapline that will appear below the title.
   */
  strapline?: string
  /**
   * Title that will appear within the article text molecule.
   */
  title: string
} & HTMLProps<HTMLDivElement>
